import { useState } from "react";
import { Check, HelpCircle, Sparkles, Store } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PhotoSummaryCard({ photo, index, itemCount, onResolveSupplier }) {
  const [busyChoice, setBusyChoice] = useState(null); // "attach:<id>" | "create"

  const doResolve = async (choice, decision, supplierId) => {
    if (!onResolveSupplier) return;
    setBusyChoice(choice);
    try {
      await onResolveSupplier(index, decision, supplierId);
    } finally {
      setBusyChoice(null);
    }
  };

  if (photo.status === "error") {
    return (
      <div
        data-testid={`receipt-photo-error-${index}`}
        className="flex items-center gap-3 p-3 rounded-lg bg-[#FCEAE8] border border-[#F2C4BE]"
      >
        <img src={photo.preview} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-semibold text-[#8E3B2A]">Foto {index + 1} non leggibile</div>
          <div className="text-xs text-[#8E3B2A]/80 truncate">{photo.error}</div>
        </div>
      </div>
    );
  }
  if (photo.status !== "done") return null;
  const meta = photo.meta || {};
  const hasSimilarProposal = !!meta.supplier_similar_proposal;
  const hasAmbiguous = !!meta.supplier_ambiguous;
  const hasQuestion = hasSimilarProposal || hasAmbiguous;

  // --- Domanda pendente: mostro il prompt al posto del badge di stato ---
  if (hasQuestion) {
    const ocrName = meta.supplier_ocr_name || meta.supplier || "(sconosciuto)";
    const candidates = hasSimilarProposal
      ? meta.supplier_similar_candidates || []
      : meta.supplier_ambiguous_candidates || [];

    return (
      <div
        data-testid={`receipt-photo-supplier-prompt-${index}`}
        className="p-3 rounded-lg bg-[#FDF6E3] border border-[#F2DEB0]"
      >
        <div className="flex items-start gap-3">
          <img src={photo.preview} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-mono font-semibold text-[#8B6518] bg-white border border-[#F2DEB0] px-1.5 py-0.5 rounded">
                Foto {index + 1}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-[#8B6518] text-white inline-flex items-center gap-0.5">
                <HelpCircle size={9} />
                Da confermare
              </span>
            </div>
            <div className="text-sm text-[#5C5F5A] break-words leading-snug">
              {hasSimilarProposal ? (
                <>
                  Ho letto <strong className="text-[#3E4034] break-words">{ocrName}</strong>.
                  {candidates.length === 1 ? (
                    <> È lo stesso di <strong className="text-[#3E4034] break-words">{candidates[0].name}</strong>?</>
                  ) : (
                    <> Somiglia a queste schede già in rubrica:</>
                  )}
                </>
              ) : (
                <>
                  Ho letto <strong className="text-[#3E4034] break-words">{ocrName}</strong>: ci sono più schede uguali in rubrica. Quale è quella giusta?
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-col gap-2">
          {/* Quasi-match: 1 candidato → 2 pulsanti chiari */}
          {hasSimilarProposal && candidates.length === 1 && (
            <>
              <Button
                data-testid={`receipt-supplier-attach-btn-${index}-0`}
                onClick={() => doResolve(`attach:${candidates[0].id}`, "attach", candidates[0].id)}
                disabled={busyChoice != null}
                className="min-h-[44px] w-full bg-[#2B5A3F] hover:bg-[#1F4530] text-white text-sm whitespace-normal break-words h-auto py-2 px-3"
              >
                Sì, è {candidates[0].name}
              </Button>
              <Button
                data-testid={`receipt-supplier-create-btn-${index}`}
                onClick={() => doResolve("create", "create")}
                disabled={busyChoice != null}
                variant="outline"
                className="min-h-[44px] w-full border-[#8B6518] text-[#8B6518] hover:bg-[#FDF6E3] text-sm whitespace-normal break-words h-auto py-2 px-3"
              >
                No, è nuovo: crea come {`\u201C${ocrName}\u201D`}
              </Button>
            </>
          )}

          {/* Quasi-match: >1 candidati → 1 pulsante per ciascuno + pulsante "nuovo" */}
          {hasSimilarProposal && candidates.length > 1 && (
            <>
              {candidates.map((c, i) => (
                <Button
                  key={c.id}
                  data-testid={`receipt-supplier-attach-btn-${index}-${i}`}
                  onClick={() => doResolve(`attach:${c.id}`, "attach", c.id)}
                  disabled={busyChoice != null}
                  className="min-h-[44px] w-full bg-[#2B5A3F] hover:bg-[#1F4530] text-white text-sm whitespace-normal break-words h-auto py-2 px-3 justify-start text-left"
                >
                  <span className="mr-2">{i + 1}.</span> {c.name}
                </Button>
              ))}
              <Button
                data-testid={`receipt-supplier-create-btn-${index}`}
                onClick={() => doResolve("create", "create")}
                disabled={busyChoice != null}
                variant="outline"
                className="min-h-[44px] w-full border-[#8B6518] text-[#8B6518] hover:bg-[#FDF6E3] text-sm whitespace-normal break-words h-auto py-2 px-3"
              >
                Nessuna: crea nuovo come {`\u201C${ocrName}\u201D`}
              </Button>
            </>
          )}

          {/* Ambiguo: solo scelta fra i candidati esistenti (nessun "crea nuovo") */}
          {hasAmbiguous && (
            <>
              {candidates.map((c, i) => (
                <Button
                  key={c.id || c.name || i}
                  data-testid={`receipt-supplier-ambiguous-btn-${index}-${i}`}
                  onClick={() => c.id && doResolve(`attach:${c.id}`, "attach", c.id)}
                  disabled={busyChoice != null || !c.id}
                  className="min-h-[44px] w-full bg-[#2B5A3F] hover:bg-[#1F4530] text-white text-sm whitespace-normal break-words h-auto py-2 px-3 justify-start text-left"
                >
                  <span className="mr-2">{i + 1}.</span> {c.name || c}
                </Button>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  // --- Stato normale: badge di stato ---
  return (
    <div
      data-testid={`receipt-photo-summary-${index}`}
      className="flex items-center gap-3 p-3 rounded-lg bg-[#E8F3EC] border border-[#C5E1D1]"
    >
      <img src={photo.preview} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono font-semibold text-[#2B5A3F] bg-white border border-[#C5E1D1] px-1.5 py-0.5 rounded">
            Foto {index + 1}
          </span>
          {meta.supplier && (
            <span className="text-xs text-[#5C5F5A] inline-flex items-center gap-1 min-w-0 break-words">
              <Store size={10} className="flex-shrink-0" />
              <span className="break-words">{meta.supplier}</span>
            </span>
          )}
          {meta.supplier_known && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-white text-[#2B5A3F] border-[#C5E1D1] inline-flex items-center gap-0.5">
              <Check size={9} />
              Riconosciuto
            </span>
          )}
          {!meta.supplier_known && meta.supplier_id && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-white text-[#8B6518] border-[#F2DEB0] inline-flex items-center gap-0.5">
              <Sparkles size={9} />
              Nuovo in rubrica
            </span>
          )}
        </div>
        <div className="text-xs text-[#5C5F5A] mt-0.5">
          {itemCount} {itemCount === 1 ? "voce trovata" : "voci trovate"}
          {meta.confidence && ` · affidabilità ${meta.confidence}`}
        </div>
      </div>
    </div>
  );
}
